import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ArrowLeft, Save, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../lib/onboardingAPI';

// Grouped income types. `key`s match the backend reveal triggers in client_onboarding.py.
const GROUPS = [
  {
    title: 'Employment & government',
    items: [
      ['salary_wages', 'Salary and wages'],
      ['allowances', 'Allowances'],
      ['director_fees', 'Director fees'],
      ['government_payments', 'Government payments'],
      ['pensions_annuities', 'Pensions / annuities'],
      ['super_income_stream', 'Superannuation income stream'],
    ],
  },
  {
    title: 'Business',
    items: [
      ['sole_trader_income', 'Sole trader income'],
      ['business_income', 'Business income'],
      ['partnership_income', 'Partnership income'],
      ['trust_distributions', 'Trust distributions'],
    ],
  },
  {
    title: 'Investments',
    items: [
      ['interest_income', 'Interest income'],
      ['dividend_income', 'Dividend income'],
      ['company_dividends', 'Company dividends'],
      ['shares', 'Shares'],
      ['managed_funds', 'Managed funds'],
      ['capital_gains', 'Capital gains'],
      ['crypto_transactions', 'Crypto transactions'],
      ['employee_share_schemes', 'Employee share schemes'],
    ],
  },
  {
    title: 'Property & overseas',
    items: [
      ['rental_property_income', 'Rental property income'],
      ['foreign_income', 'Foreign income'],
    ],
  },
  { title: 'Other', items: [['other_income', 'Other income']] },
];

// Which selections reveal which detailed onboarding sections (mirrors backend).
const REVEALS = [
  { label: 'Rental Properties', triggers: ['rental_property_income'] },
  { label: 'Investments & CGT', triggers: ['capital_gains', 'dividend_income', 'shares', 'managed_funds', 'crypto_transactions', 'employee_share_schemes'] },
  { label: 'Foreign Income', triggers: ['foreign_income'] },
  { label: 'Business Activity', triggers: ['sole_trader_income', 'business_income'] },
];

export default function IncomeSourcesForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!clientId) return;
    onboardingAPI.getSection(clientId, 'income_sources')
      .then((res) => setSelected(res.data.response_data?.selected_income_types || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId]);

  const toggle = (k) => setSelected((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);

  const save = async (status) => {
    if (status === 'completed' && selected.length === 0) {
      toast.error('Please select at least one income type');
      return;
    }
    setSaving(true);
    try {
      await onboardingAPI.saveSection(clientId, 'income_sources', {
        response_data: { selected_income_types: selected }, status,
      });
      toast.success(status === 'completed' ? 'Income sources saved' : 'Saved — you can return any time');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;

  const revealed = REVEALS.filter((r) => r.triggers.some((t) => selected.includes(t)));

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Income Sources</CardTitle>
          <CardDescription>Select all income types that apply. Your selections automatically reveal the matching detailed sections.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <p className="text-sm font-semibold text-foreground/90 mb-2">{g.title}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {g.items.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer hover:bg-muted/50">
                    <Checkbox checked={selected.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`income-${k}`} />
                    <span className="text-sm text-foreground/90">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}

          {revealed.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Based on your selections, these sections will appear on your checklist: <strong>{revealed.map((r) => r.label).join(', ')}</strong>.</span>
            </div>
          )}

          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={() => save('in_progress')} disabled={saving} data-testid="income-save">
              <Save className="w-4 h-4 mr-2" /> Save &amp; return later
            </Button>
            <Button onClick={() => save('completed')} disabled={saving} data-testid="income-complete">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
